import { toInteger } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import PptxGenJS from '@meitu/js-to-pptx';
import dom2Image, { newUtil } from '@/utils/dom-to-image';
import { createCanvas } from '@/utils/file-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import { renderLayerDom } from '@/utils/render-kit';
import { pxToInch } from '@/utils/unit-kit';
import { uploadFileToObs } from '@/utils/upload-kit';
import { IChartLayer, ILayerBehavior } from '@/version-types';
import { savePPTChart } from '../utils/ppt';
import { ChartLayerPureView, checkChartIsEmpty } from '../view/chart-layer';

type Behaviors = Pick<
    ILayerBehavior,
    'onBeforeSaveAsync' | 'savePPTLayerAsync' | 'getResultCanvasAsync' | 'getLayerImageAsync'
>;

interface CanvasAndFile {
    file: File;
    canvas: HTMLCanvasElement;
}

const getChartLayerCanvasAndFile = async (layer: IChartLayer): Promise<CanvasAndFile> => {
    const containerRef: { current: HTMLDivElement | null } = { current: null };
    const renderPromise = () =>
        new Promise<() => void>((resolve) => {
            const cleanup = renderLayerDom(
                <div
                    ref={containerRef}
                    className="tempChartWrapper"
                    style={{ width: 'max-content', height: 'max-content' }}
                >
                    <ChartLayerPureView layer={layer} renderer="canvas" onRenderEnd={() => resolve(cleanup)} />
                </div>,
            );
        });
    const cleanup = await renderPromise();
    const containerEl = containerRef.current!;
    const canvas = checkChartIsEmpty(layer)
        ? await dom2Image.toCanvas(containerEl)
        : containerEl.querySelector('canvas');
    if (!canvas) {
        console.log('===tempChartWrapper', containerRef.current);
        throw new Error('canvas not found');
    }
    const resultCanvas = createCanvas(layer.width, layer.height, canvas).canvas;
    cleanup();
    const blob = await newUtil().canvasToBlob(canvas);
    const file = new File([blob!], uuidv4(), {
        type: 'image/png',
    });
    return {
        file,
        canvas: resultCanvas,
    };
};

const getChartLayerScreenshotUrl = async (layer: IChartLayer) => {
    const { file } = await getChartLayerCanvasAndFile(layer);
    const { url: screenshotUrl } = await uploadFileToObs(file);
    return screenshotUrl;
};

export const createSaveBehaviors = (frameworkIdentity: string) => {
    const behaviors: Behaviors = {
        async getLayerImageAsync(layer) {
            const { canvas } = await getChartLayerCanvasAndFile(layer as IChartLayer);
            return canvas;
        },

        async onBeforeSaveAsync(_layer) {
            const layer = _layer as IChartLayer;
            deleteExtraInfo(layer);
            const screenshotUrl = await getChartLayerScreenshotUrl(layer);
            layer.data.screenshotUrl = screenshotUrl;
        },

        async getResultCanvasAsync(layer, resultSize) {
            // console.time('===========生成时间');
            const { width, height, left, top, rotate } = layer;
            const { width: stageWidth, height: stageHeight } = resultSize;
            const resultWidth = toInteger(width);
            const resultHeight = toInteger(height);
            const centerPoint = {
                x: resultWidth / 2 + left,
                y: resultHeight / 2 + top,
            };
            const drawOffsetPoint = {
                x: -resultWidth / 2,
                y: -resultHeight / 2,
            };
            const layerImage = await behaviors.getLayerImageAsync?.(layer, false);
            if (!layerImage) return;
            const { canvas: destCanvas, ctx: destCtx } = createCanvas(stageWidth, stageHeight);
            destCtx.translate(centerPoint.x, centerPoint.y);
            destCtx.rotate((Math.PI / 180) * rotate);
            destCtx.drawImage(
                layerImage,
                0,
                0,
                resultWidth,
                resultHeight,
                drawOffsetPoint.x,
                drawOffsetPoint.y,
                resultWidth,
                resultHeight,
            );
            // console.timeEnd('===========生成时间');
            return destCanvas;
        },

        async savePPTLayerAsync(slide, _layer, _, __, ___, outFontFamilyArr) {
            const layer = _layer as IChartLayer;

            if (checkChartIsEmpty(layer)) {
                const { left, top, width, height, rotate } = layer;
                const options: PptxGenJS.ImageProps = {
                    x: pxToInch(left),
                    y: pxToInch(top),
                    w: pxToInch(width),
                    h: pxToInch(height),
                    rotate: rotate > 0 ? rotate : 360 + rotate, // 0 - 360
                };

                const canvas = await behaviors.getLayerImageAsync?.(layer);

                options.data = canvas?.toDataURL() || '';

                slide.addImage(options);
                return;
            }

            savePPTChart(layer, slide, outFontFamilyArr);
        },
    };

    return behaviors;
};
